import { useDeferredValue, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { BlogPostCard, Button, Container, StyledGrid, Title, Tab, TabList, Tabs, FormInput } from '@tryhackme/thm-ui-components';
import { CTABanner } from 'src/common/components/cta-banner/cta-banner';
import { useGetAllPostsQuery, useGetPostsQuery } from 'src/features/api/ghost-slice';
import { ReactComponent as BannerImage } from 'src/images/svgs/community-dark-mode.svg';
import { SEO } from 'src/common/components/seo';
import { GhostPost } from 'src/common/interfaces';
import { TOTAL_USERS_WITH_MILLION_UNIT } from 'src/common/constants/user';
import { getPdfUrls } from './helpers';
import { StyledBanner, StyledBannerContent, StyledFiltersWrapper, StyledCardsWrapper, StyledSearch, StyledButtonWrapper, StyledResourcesPage } from './resources.styles';
const getTitleName = (category: string) => {
  switch (category) {
    case 'newsroom':
      {
        return 'Newsroom';
      }
    case 'blog':
      {
        return 'Blog';
      }
    case 'success-story':
      {
        return 'Individual Stories';
      }
    case 'business-resource':
      {
        return 'Business Stories';
      }
    default:
      {
        throw new Error('Invalid category');
      }
  }
};
export const Resources = () => {
  const {
    category = 'blog'
  } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const deferredSearchValue = useDeferredValue(searchValue);
  const {
    data,
    isError,
    isFetching
  } = useGetPostsQuery({
    filter: `tags:${category}`,
    page
  });
  const {
    data: postForSearch,
    isError: isErrorAllPost,
    isFetching: isFetchingAllPost
  } = useGetAllPostsQuery({
    formats: 'html,plaintext'
  });
  const posts = data?.posts;
  const allPosts = postForSearch?.posts;
  const handleSearchValue = (value: string) => setSearchValue(value);
  const filteredPosts = useMemo(() => {
    if (!allPosts || !deferredSearchValue) {
      return posts;
    }
    const exp = new RegExp(deferredSearchValue, 'i');
    return allPosts?.filter((post: GhostPost) => exp.test(post?.title) || exp.test(post?.plaintext || ''));
  }, [posts, allPosts, deferredSearchValue]);
  const handleShowMoreButton = () => {
    const nextPage = data?.meta?.pagination?.next;
    return nextPage && setPage(nextPage);
  };
  const handleCategoryTab = (path: string) => {
    setPage(1);
    setSearchValue('');
    navigate(path);
  };
  return <>
      <StyledResourcesPage data-sentry-element="StyledResourcesPage" data-sentry-source-file="resources.tsx">
        <SEO title="Resources" description="TryHackMe is a free online platform for learning cyber security, using hands-on exercises and labs, all through your browser!" data-sentry-element="SEO" data-sentry-source-file="resources.tsx" />
        <StyledBanner data-sentry-element="StyledBanner" data-sentry-source-file="resources.tsx">
          <Container data-sentry-element="Container" data-sentry-source-file="resources.tsx">
            <StyledBannerContent data-sentry-element="StyledBannerContent" data-sentry-source-file="resources.tsx">
              <Title titleText={getTitleName(category)} subTitleText={<>
                    Hands-on cyber security training trusted by over <span>{TOTAL_USERS_WITH_MILLION_UNIT} users</span>{' '}
                    and <span>640+ organisations</span>.
                  </>} isLeftAlign isInBanner data-sentry-element="Title" data-sentry-source-file="resources.tsx" />
              <BannerImage data-sentry-element="BannerImage" data-sentry-source-file="resources.tsx" />
            </StyledBannerContent>
          </Container>
        </StyledBanner>
        <Container data-sentry-element="Container" data-sentry-source-file="resources.tsx">
          <StyledFiltersWrapper data-sentry-element="StyledFiltersWrapper" data-sentry-source-file="resources.tsx">
            <Tabs value={category} defaultValue="blog" data-sentry-element="Tabs" data-sentry-source-file="resources.tsx">
              <TabList data-sentry-element="TabList" data-sentry-source-file="resources.tsx">
                <Tab onClick={() => handleCategoryTab('/resources/blog')} value="blog" data-sentry-element="Tab" data-sentry-source-file="resources.tsx">
                  Blog
                </Tab>
                <Tab onClick={() => handleCategoryTab('/resources/success-story')} value="success-story" data-sentry-element="Tab" data-sentry-source-file="resources.tsx">
                  Individual Stories
                </Tab>
                <Tab onClick={() => handleCategoryTab('/resources/newsroom')} value="newsroom" data-sentry-element="Tab" data-sentry-source-file="resources.tsx">
                  Newsroom
                </Tab>
                <Tab onClick={() => handleCategoryTab('/resources/business-resource')} value="business-resource" data-sentry-element="Tab" data-sentry-source-file="resources.tsx">
                  Business Stories
                </Tab>
              </TabList>
            </Tabs>
            <StyledSearch data-sentry-element="StyledSearch" data-sentry-source-file="resources.tsx">
              <FormInput placeholder="Find posts by a keyword..." leftAdornment={<FontAwesomeIcon icon={faSearch} />} onChange={e => {
              const targetValue = e.target.value;
              if (targetValue.length >= 3) handleSearchValue(targetValue);
            }} data-sentry-element="FormInput" data-sentry-source-file="resources.tsx" />
            </StyledSearch>
          </StyledFiltersWrapper>

          {isError || isErrorAllPost && <StyledCardsWrapper>
                <p>We haven&apos;t found any posts</p>
              </StyledCardsWrapper>}

          {filteredPosts?.length === 0 && <StyledCardsWrapper>
              <p>We haven&apos;t found any posts</p>
            </StyledCardsWrapper>}

          {isFetching || isFetchingAllPost ? <StyledGrid gridTemplateColumns="col-3" gap="6rem 2rem">
              <BlogPostCard key="1" isLoading href="/" />
              <BlogPostCard key="2" isLoading href="/" />
              <BlogPostCard key="3" isLoading href="/" />
            </StyledGrid> : <StyledGrid gridTemplateColumns="col-3" gap="4.1rem 2rem">
              {filteredPosts?.map((post: GhostPost) => {
            const postUrl = post?.primary_tag?.slug === 'business-resource' ? `${getPdfUrls(post)}` : `/resources/blog/${post.slug}`;
            const lastTag = post?.tags?.slice(-1).pop()?.name === 'business-resource' ? 'Business' : post?.tags?.slice(-1).pop()?.name;
            return <BlogPostCard key={post.slug} articleTitle={post.title} imageProps={{
              src: post.feature_image ?? '',
              alt: post.slug
            }} readingTime={`${post.reading_time} min read`} extract={post.custom_excerpt} category={lastTag} href={postUrl} target="_blank" />;
          })}
            </StyledGrid>}
          {data?.meta?.pagination?.next !== null && <StyledButtonWrapper>
              <Button variant="outlined" color="primary" onClick={handleShowMoreButton}>
                Show More
              </Button>
            </StyledButtonWrapper>}
        </Container>
      </StyledResourcesPage>
      <CTABanner data-sentry-element="CTABanner" data-sentry-source-file="resources.tsx" />
    </>;
};